import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { ChevronDown, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/theme-provider";
import logoImg from "@assets/Untitled_design_(9)_1773925932166.png";
import logoDarkImg from "@assets/Untitled_design_(10)_1773926263699.png";

export function SeoPageHeader() {
  const { theme } = useTheme();
  const logo = theme === "dark" ? logoDarkImg : logoImg;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    function handleOutsideClick(e: MouseEvent | TouchEvent) {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    }
    function handleScroll() {
      setMobileMenuOpen(false);
    }
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [mobileMenuOpen]);

  return (
    <header ref={headerRef} className="border-b border-border/40 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link href="/">
          <img src={logo} alt="The Proper Tool" className="h-8 w-auto" />
        </Link>
        <div className="flex items-center gap-4">
          {/* Desktop nav */}
          <div className="hidden sm:flex items-center gap-4">
            <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
            <div className="relative group">
              <button className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                Blog &amp; Guides <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                <div className="bg-background border border-border rounded-lg shadow-lg min-w-56 py-1">
                  <Link href="/blog" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Blog</Link>
                  <div className="border-t border-border my-1" />
                  <Link href="/guides/" className="block px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60 hover:text-foreground transition-colors">Guides</Link>
                  <Link href="/guides/quoting-for-uk-builders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Quoting for UK Builders</Link>
                  <Link href="/guides/running-a-building-business-uk" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Running a Building Business</Link>
                </div>
              </div>
            </div>
            <Link href="/glossary" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Glossary</Link>
            <Link href="/uk-builder-quoting-survey-2026" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Research</Link>
            <Link href="/comparisons" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Compare</Link>
            <div className="relative group">
              <button className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                Smart Quoting <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute top-full right-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                <div className="bg-background border border-border rounded-lg shadow-lg min-w-52 py-1">
                  <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Smart Quoting</p>
                  <Link href="/auto-quote" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Auto Quote</Link>
                  <Link href="/ai-quoting-software" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">AI Quoting App</Link>
                  <Link href="/ai-takeoff-software" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">AI Takeoff Software</Link>
                  <Link href="/quote-from-photo" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Quote from Photo</Link>
                  <Link href="/quote-from-drawings/" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Quote from Drawings</Link>
                  <Link href="/quote-from-description/" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Quote from Description</Link>
                </div>
              </div>
            </div>
            <div className="relative group">
              <button className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                The App <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute top-full right-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                <div className="bg-background border border-border rounded-lg shadow-lg min-w-60 py-1">
                  <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Product Features</p>
                  <Link href="/auto-quote" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Smart Quoting</Link>
                  <Link href="/estimating-software-for-builders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Estimating Software</Link>
                  <Link href="/construction-estimating-software" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Cost Estimating</Link>
                  <Link href="/quoting-app-for-builders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Mobile App</Link>
                  <div className="border-t border-border my-1" />
                  <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Who It's For</p>
                  <Link href="/quoting-software-for-contractors" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Contractors</Link>
                  <Link href="/tradesman-quoting-tool" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Tradesmen</Link>
                  <Link href="/quoting-software-for-electricians" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Electricians</Link>
                  <Link href="/quoting-software-for-plumbers" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Plumbers</Link>
                  <Link href="/quoting-software-for-roofers" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Roofers</Link>
                  <Link href="/quoting-software-for-plasterers" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Plasterers</Link>
                  <Link href="/quoting-software-for-decorators" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Decorators</Link>
                  <Link href="/job-quoting-software" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Job Quoting</Link>
                  <Link href="/for-small-builders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Small Builders</Link>
                  <Link href="/for-sole-traders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Sole Traders</Link>
                  <div className="border-t border-border my-1" />
                  <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Compare</p>
                  <Link href="/best/quoting-software-for-uk-builders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Compare All Tools</Link>
                  <Link href="/tradify-alternative" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Tradify Alternative</Link>
                  <Link href="/buildxact-alternative" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Buildxact Alternative</Link>
                  <Link href="/simpro-alternative" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">simPRO Alternative</Link>
                  <Link href="/estimatorxpress-alternative" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">EstimatorXpress Alternative</Link>
                  <Link href="/pricejob-alternative" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">PriceAJob Alternative</Link>
                  <Link href="/estimage-alternative" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">esti-mate Alternative</Link>
                </div>
              </div>
            </div>
          </div>
          <Button size="sm" asChild className="hidden sm:inline-flex">
            <Link href="/login">Start free trial</Link>
          </Button>
          {/* Mobile hamburger */}
          <button
            className="sm:hidden flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="button-mobile-menu"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden border-t border-border bg-background">
          <div className="px-4 py-3 space-y-1">
            <Link
              href="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-home"
            >
              Home
            </Link>
            <Link
              href="/guides/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-guides"
            >
              Guides
            </Link>
            <p className="px-3 pt-1 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Guides</p>
            <Link
              href="/guides/quoting-for-uk-builders"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-guide-quoting"
            >
              Quoting for UK Builders
            </Link>
            <Link
              href="/guides/running-a-building-business-uk"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-guide-running"
            >
              Running a Building Business
            </Link>
            <Link
              href="/glossary"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-glossary"
            >
              Glossary
            </Link>
            <Link
              href="/uk-builder-quoting-survey-2026"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-research"
            >
              Research &amp; Data
            </Link>
            <Link
              href="/comparisons"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-compare"
            >
              Compare
            </Link>

            <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Smart Quoting</p>
            <Link
              href="/auto-quote"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-auto-quote"
            >
              Auto Quote
            </Link>
            <Link
              href="/ai-quoting-software"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-ai-quoting-software"
            >
              AI Quoting App
            </Link>
            <Link
              href="/ai-takeoff-software"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-ai-takeoff-software"
            >
              AI Takeoff Software
            </Link>
            <Link
              href="/quote-from-photo"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-quote-from-photo"
            >
              Quote from Photo
            </Link>
            <Link
              href="/quote-from-drawings/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-quote-from-drawings"
            >
              Quote from Drawings
            </Link>
            <Link
              href="/quote-from-description/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-quote-from-description"
            >
              Quote from Description
            </Link>

            <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">The App: Product Features</p>
            <Link
              href="/auto-quote"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-quoting-software"
            >
              Smart Quoting
            </Link>
            <Link
              href="/estimating-software-for-builders"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-estimating-builders"
            >
              Estimating Software
            </Link>
            <Link
              href="/construction-estimating-software"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-estimating-software"
            >
              Cost Estimating
            </Link>
            <Link
              href="/quoting-app-for-builders"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-quoting-app"
            >
              Mobile App
            </Link>

            <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">The App: Who It's For</p>
            <Link
              href="/quoting-software-for-contractors"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-quoting-contractors"
            >
              For Contractors
            </Link>
            <Link
              href="/tradesman-quoting-tool"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-tradesman-tool"
            >
              For Tradesmen
            </Link>
            <Link
              href="/quoting-software-for-electricians"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-electricians"
            >
              For Electricians
            </Link>
            <Link
              href="/quoting-software-for-plumbers"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-plumbers"
            >
              For Plumbers
            </Link>
            <Link
              href="/quoting-software-for-roofers"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-roofers"
            >
              For Roofers
            </Link>
            <Link
              href="/quoting-software-for-plasterers"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-plasterers"
            >
              For Plasterers
            </Link>
            <Link
              href="/quoting-software-for-decorators"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-decorators"
            >
              For Decorators
            </Link>
            <Link
              href="/job-quoting-software"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-job-quoting"
            >
              For Job Quoting
            </Link>
            <Link
              href="/for-small-builders"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-for-small-builders"
            >
              For Small Builders
            </Link>
            <Link
              href="/for-sole-traders"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-for-sole-traders"
            >
              For Sole Traders
            </Link>

            <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Compare</p>
            <Link
              href="/best/quoting-software-for-uk-builders"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-best-quoting"
            >
              Compare All Tools
            </Link>
            <Link
              href="/tradify-alternative"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-tradify-alt"
            >
              Tradify Alternative
            </Link>
            <Link
              href="/buildxact-alternative"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-buildxact-alt"
            >
              Buildxact Alternative
            </Link>
            <Link
              href="/simpro-alternative"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-simpro-alt"
            >
              simPRO Alternative
            </Link>
            <Link
              href="/estimatorxpress-alternative"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-estimatorxpress-alt"
            >
              EstimatorXpress Alternative
            </Link>
            <Link
              href="/pricejob-alternative"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-pricejob-alt"
            >
              PriceAJob Alternative
            </Link>
            <Link
              href="/estimage-alternative"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="mobile-link-estimage-alt"
            >
              esti-mate Alternative
            </Link>

            <div className="pt-2 pb-1">
              <Link
                href="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center bg-primary text-primary-foreground font-semibold rounded-lg px-4 py-3 text-sm"
                data-testid="mobile-button-start-trial"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
